import { useEffect, useRef, useState } from "react";
import {
  TrendingUp, Loader2, Sparkles, ExternalLink, RefreshCw, Clapperboard, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EFFORT_STYLE = {
  niedrig: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mittel: "bg-amber-50 text-amber-700 border-amber-200",
  hoch: "bg-rose-50 text-rose-700 border-rose-200",
};

export function TrendScout({ accountKey }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [researching, setResearching] = useState(false);
  const [adopted, setAdopted] = useState({});
  const pollRef = useRef(null);

  const loadLatest = () =>
    api.get("/trends/latest", { params: { account: accountKey } })
      .then(({ data }) => { setReport(data.empty ? null : data); setLoading(false); })
      .catch(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    setAdopted({});
    loadLatest();
    return () => clearTimeout(pollRef.current);
  }, [accountKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const research = async () => {
    setResearching(true);
    try {
      const { data } = await api.post("/trends/generate", { account_key: accountKey });
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/trends/jobs/${data.job_id}`);
          if (job.status === "done") {
            await loadLatest();
            setResearching(false);
            toast.success("Trend-Recherche abgeschlossen ✨");
          } else if (job.status === "error") {
            setResearching(false);
            toast.error(job.error || "Recherche fehlgeschlagen");
          } else {
            pollRef.current = setTimeout(poll, 4000);
          }
        } catch {
          pollRef.current = setTimeout(poll, 5000);
        }
      };
      pollRef.current = setTimeout(poll, 5000);
    } catch (e) {
      setResearching(false);
      toast.error(e.response?.data?.detail || "Recherche konnte nicht gestartet werden");
    }
  };

  const adopt = async (trend, idx) => {
    try {
      await api.post("/content", {
        account_key: accountKey,
        title: trend.title,
        content_type: "reel",
        status: "idea",
        hook: trend.hook_example || "",
        caption: trend.adaptation || "",
        source_link: (trend.sources || [])[0] || "",
        flags: ["trend"],
      });
      setAdopted((a) => ({ ...a, [idx]: true }));
      toast.success(`„${trend.title}" als Idee in die Pipeline übernommen ✨`);
    } catch {
      toast.error("Übernehmen fehlgeschlagen");
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div data-testid="trend-scout" className="mt-6 min-w-0 max-w-full">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 max-w-2xl">
          <h2 className="font-head font-bold text-lg text-neutral-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-fuchsia-500" /> KI-Trend-Scout
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            Die KI recherchiert im Web, welche Reel-Trends gerade viral gehen — und übersetzt sie in drehbare Ideen für diesen Kanal. Aktualisiert sich danach automatisch wöchentlich.
          </p>
        </div>
        <button
          data-testid="trend-research-btn"
          onClick={research}
          disabled={researching}
          className="inline-flex max-w-full items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-60"
        >
          {researching ? <Loader2 className="h-4 w-4 animate-spin" /> : report ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          {researching ? "KI recherchiert im Web…" : report ? "Neu recherchieren" : "Trends recherchieren"}
        </button>
      </div>

      {researching && (
        <div data-testid="trend-researching-hint" className="mt-4 rounded-xl border border-fuchsia-200 bg-fuchsia-50/60 p-4 text-sm text-fuchsia-800">
          Die KI durchsucht gerade aktuelle Trend-Quellen und passt die Ergebnisse an euren Kanal an — das dauert 1–2 Minuten. Du kannst die Seite währenddessen verlassen.
        </div>
      )}

      {!report && !researching && (
        <div data-testid="trend-empty" className="mt-6 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center">
          <TrendingUp className="h-8 w-8 text-neutral-300 mx-auto" />
          <p className="text-sm text-neutral-500 mt-3">Noch keine Trend-Recherche für diesen Kanal.<br />Starte die erste — danach hält der Scout die Trends wöchentlich frisch.</p>
        </div>
      )}

      {report && (
        <>
          <div className="mt-5 min-w-0 max-w-full rounded-xl bg-neutral-900 text-white p-4 sm:p-5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
              Trend-Lage · {new Date(report.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
            </div>
            <p data-testid="trend-summary" className="mt-1.5 break-words text-sm leading-relaxed">{report.summary}</p>
          </div>

          <div className="mt-4 grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
            {(report.trends || []).map((t, i) => (
              <div key={i} data-testid={`trend-card-${i}`} className="flex min-w-0 max-w-full flex-col rounded-xl border border-neutral-200 bg-white p-4 sm:p-5">
                <div className="flex min-w-0 flex-col items-start gap-2.5 sm:flex-row sm:justify-between sm:gap-3">
                  <h3 className="min-w-0 break-words font-head text-[16px] font-semibold leading-snug text-neutral-900">{t.title}</h3>
                  <div className="flex max-w-full flex-wrap items-center gap-1.5 sm:shrink-0 sm:justify-end">
                    {t.format && <span className="max-w-full break-words rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold leading-4 text-indigo-700">{t.format}</span>}
                    {t.effort && <span className={`max-w-full break-words rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-4 ${EFFORT_STYLE[t.effort] || "bg-neutral-100 text-neutral-500 border-neutral-200"}`}>Aufwand: {t.effort}</span>}
                  </div>
                </div>
                <p className="mt-2 break-words text-sm leading-relaxed text-neutral-600">{t.description}</p>
                {t.why_it_works && (
                  <p className="mt-2 break-words text-[13px] text-neutral-500"><span className="font-semibold text-neutral-700">Warum es zieht:</span> {t.why_it_works}</p>
                )}
                <div className="mt-3 min-w-0 rounded-lg border border-fuchsia-100 bg-fuchsia-50/70 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-fuchsia-600">So sieht das bei euch aus</div>
                  <p className="mt-1 break-words text-[13px] leading-relaxed text-neutral-800">{t.adaptation}</p>
                  {t.hook_example && (
                    <p className="mt-1.5 break-words text-[13px] italic text-neutral-700">Hook: „{t.hook_example}"</p>
                  )}
                </div>
                <div className="mt-3 flex min-w-0 flex-col items-start gap-3 border-t border-neutral-100 pt-3 sm:flex-row sm:items-center sm:gap-2">
                  <button
                    data-testid={`trend-adopt-${i}`}
                    onClick={() => adopt(t, i)}
                    disabled={adopted[i]}
                    className={`inline-flex max-w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      adopted[i] ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-neutral-900 text-white hover:bg-neutral-800"
                    }`}
                  >
                    {adopted[i] ? <Check className="h-4 w-4" /> : <Clapperboard className="h-4 w-4" />}
                    {adopted[i] ? "In Pipeline ✓" : "Als Idee übernehmen"}
                  </button>
                  <div className="flex max-w-full flex-wrap items-center gap-2 sm:ml-auto sm:justify-end">
                    {(t.sources || []).slice(0, 2).map((s, j) => (
                      <a key={j} href={s} target="_blank" rel="noopener noreferrer" title={s}
                        className="text-[11px] text-neutral-400 hover:text-blue-600 inline-flex items-center gap-0.5">
                        <ExternalLink className="h-3 w-3" /> Quelle {j + 1}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
